import React, { useContext } from 'react'
import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { Box, ContentBlock } from '@careguide/theme'
import Text from 'components/base/Text'
import ProfileCard from './ProfileCard/ProfileCard'
import {
  TabletAndUpOnlyAdContainer,
  MobileOnlyBox,
  TabletAndUpOnlyBox,
  CTAContainer,
  ResumeLink,
  StickyContainer,
} from './Listing.style'
import ListingProfile from './ListingProfile'
import Breadcrumbs from './Breadcrumbs'
import { ViewerContext } from 'contexts/ViewerContext'
import ListingDescription from './ListingDescription'
import ListingQuery from './query.listing.gql'
import { ListingSEO } from './ListingSEO'
import { CareGuidePageProps } from 'types/careguide-page'
import HireReasons from './HireReasons'
import Photos from './Photos'
import resumeSrc from 'Images/SVG/resume.svg'
import ListingCTA from './ListingCTA'
import ListingFacets from './ListingFacets'
import FacebookShare from './FacebookShare'
import { FacetInformation } from 'types/listing-facet'
import Recommendations from './Recommendations/Recommendations'
import FooterLinks from './FooterLinks/FooterLinks'
import Messenger from './Messenger/Messenger'
import ListingLoadingSkeleton from './LoadingSkeleton/LoadingSkeleton'
import { ListingStructuredData } from './ListingStructuredData'
import { Stack } from '@careguide/stack'
import { Heading, Subheading } from '@careguide/typography'
import { useExperiment } from 'components/experiments/peekaboo'
import { CreditsBanner, getLimitedApplicationsConfig } from 'components/LimitedApplications'
import useAnalyticsPageTracker from 'hooks/useAnalyticsPageTracker'
import { DFPSlotsProvider, AdSlot } from 'react-dfp'
import { BusinessModelEnum } from 'graph/globalTypes'
import ListingUpgradeCTA from './ListingUpgradeCTA'
import { Listing as ListingType, ListingVariables } from 'graph/Listing'

const Listing = ({ pageProps }: CareGuidePageProps<{ analyticsPageType: string }>) => {
  const viewer = useContext(ViewerContext)
  const router = useRouter()
  useAnalyticsPageTracker(pageProps.analyticsPageType, router.asPath)
  const { loading, error, data, refetch } = useQuery<ListingType, ListingVariables>(ListingQuery, {
    variables: { slug: router.query.slug as string },
  })

  if (loading) {
    return <ListingLoadingSkeleton />
  }

  const listing = data?.listing
  const isMessagingAllowed =
    (listing?.site?.businessModel === BusinessModelEnum.VIP ||
      listing?.site?.businessModel === BusinessModelEnum.ACTIVE_JOBS) &&
    !viewer?.user?.isProvider
      ? viewer?.isSubscribed
      : true
  const isListingReady = !loading && !error && listing
  const isUserLoggedIn = !!viewer?.user?.userId
  const isListingCTAVisible = !isUserLoggedIn || !viewer.hasActiveListing
  const isUpgradeCTAVisible = !isMessagingAllowed && !isListingCTAVisible
  const isMessengerVisible = isUserLoggedIn && viewer.hasActiveListing && isMessagingAllowed
  const similarListings = listing?.similarListings ?? []
  const hasSimilarListings = similarListings.length > 0
  const sisterListings = listing?.sisterListings ?? []
  const hasSisterListings = sisterListings.length > 0
  // @ts-ignore
  const hasHireReasons = listing?.linkifiedHireReasons?.length > 0
  const resumeText = isUserLoggedIn ? 'View their resume' : 'Please sign up to view resumes'
  const resumeLink = isUserLoggedIn ? listing?.resumeUrl : listing?.stepOnePath
  const shouldShowResume = !listing?.job && resumeLink
  // @ts-ignore
  const hasPhotos = listing?.photos.length > 0
  const facebookShareLink = listing?.facebookShareLink
  const avatarLarge = listing?.avatar?.largeUrl
  const facetInformationList = (listing?.facetInformation || []) as Array<FacetInformation>
  const recommenders = listing?.recommenders
  const listingOwnerName = listing?.ownerName
  const siteKeyword = listing?.site?.keyword
  const hasFacetInformation = facetInformationList.length > 0
  const listingCityName = listing?.city?.name
  const listingStateName = listing?.state?.name
  const listingUser = listing?.user
  const breadcrumbs = listing?.breadcrumbs
  const userType = viewer.user?.userType

  const limitedApplications = useExperiment('Limited Applications')
  const limitedApplicationsVariant = limitedApplications.experiment?.variation?.key
  const isFreeUser = !viewer?.isSubscribed

  return (
    <DFPSlotsProvider dfpNetworkId='23314643'>
      <ListingSEO
        // @ts-ignore
        ogTitle={listing?.name}
        // @ts-ignore
        description={listing?.description}
        // @ts-ignore
        title={listing?.title}
        // @ts-ignore
        canonicalUrl={pageProps.canonicalUrl}
        // @ts-ignore
        avatarLarge={avatarLarge}
      />
      <ListingStructuredData data={listing?.jsonldBreadcrumbsData as string} />
      <ListingStructuredData data={listing?.jsonldJobData as string} />
      <Box minWidth='100%' backgroundColor='gray.0' aria-hidden>
        <TabletAndUpOnlyAdContainer css={{ paddingTop: 0 }} justifyContent='center'>
          <AdSlot adUnit='listing-leaderboard' sizes={[[728, 90]]} />
        </TabletAndUpOnlyAdContainer>
        <MobileOnlyBox minWidth='100%' display='inline-flex' justifyContent='center'>
          <AdSlot adUnit='listing-leaderboard' sizes={[[320, 50]]} />
        </MobileOnlyBox>
      </Box>
      <ContentBlock maxWidth='896px' paddingX={{ _: '4' }} paddingTop='4'>
        {isFreeUser && limitedApplicationsVariant && !listing?.hasConversationWithRequester && (
          <>
            <CreditsBanner
              isProvider={userType === 'provider'}
              // @ts-ignore
              personName={listingUser?.firstName}
              {...getLimitedApplicationsConfig(limitedApplicationsVariant)}
            />
            <div style={{ height: '1rem' }} />
          </>
        )}
        <Box display='flex' flexDirection={{ _: 'column', tablet: 'row' }}>
          <Box flexBasis={{ _: '70%', tablet: '57%' }} mb={{ _: '6', tablet: '0' }}>
            {isListingReady && (
              <Stack gap='6'>
                <Stack gap='5'>
                  <Breadcrumbs
                    // @ts-ignore
                    breadcrumbs={breadcrumbs}
                  />
                  <ListingProfile listing={listing} />
                </Stack>
                <MobileOnlyBox>
                  {isUpgradeCTAVisible && <ListingUpgradeCTA name={listing?.user?.firstName!} />}
                  {isListingCTAVisible && (
                    <CTAContainer>
                      <ListingCTA listing={data?.listing} viewer={viewer} />
                    </CTAContainer>
                  )}
                </MobileOnlyBox>

                {hasHireReasons && (
                  <HireReasons
                    // @ts-ignore
                    hireReasons={listing?.linkifiedHireReasons}
                  />
                )}

                <ListingDescription
                  // @ts-ignore
                  description={listing.description}
                  // @ts-ignore
                  loggedInDescriptionHeading={listing.loggedInDescriptionHeading}
                  // @ts-ignore
                  loggedOutDescriptionHeading={listing.loggedOutDescriptionHeading}
                />

                {shouldShowResume && (
                  <Stack direction='horizontal'>
                    <Box as='img' src={resumeSrc} mr={2} height='16px' aria-hidden />
                    <ResumeLink
                      // @ts-ignore
                      href={resumeLink}
                    >
                      {resumeText}
                    </ResumeLink>
                  </Stack>
                )}

                {hasPhotos && (
                  <Photos
                    // @ts-ignore
                    photos={listing.photos}
                  />
                )}

                {hasFacetInformation &&
                  facetInformationList.map((facetInfo) => (
                    <Stack key={facetInfo.groupName} gap='3'>
                      {facetInfo.heading && <Heading as='h2'>{facetInfo.heading}</Heading>}
                      {facetInfo.facetGroups.map((facetGroup) => (
                        <Stack key={`${facetGroup.groupName}`} gap='2'>
                          {facetGroup.heading && (
                            <Subheading size='2'>{facetGroup.heading}</Subheading>
                          )}
                          <ListingFacets facets={facetGroup.facets} />
                        </Stack>
                      ))}
                    </Stack>
                  ))}

                <Recommendations
                  // @ts-ignore
                  recommenders={recommenders}
                  // @ts-ignore
                  listingOwnerName={listingOwnerName}
                  // @ts-ignore
                  siteKeyword={siteKeyword}
                />

                <FacebookShare
                  // @ts-ignore
                  facebookShareLink={facebookShareLink}
                />

                {isListingCTAVisible && (
                  <CTAContainer>
                    <ListingCTA listing={data?.listing} viewer={viewer} />
                  </CTAContainer>
                )}
                {hasSisterListings && (
                  <Stack gap='2'>
                    <Heading as='h2'>My Other Profiles</Heading>
                    <Stack gap='4' role='list'>
                      {sisterListings.map((listing, i) => (
                        <ProfileCard key={i} profile={listing} features={{ sisterProfile: true }} />
                      ))}
                    </Stack>
                  </Stack>
                )}
                <FooterLinks listing={listing} />
              </Stack>
            )}
          </Box>
          <Box flex='1' ml={{ _: 0, tablet: '3rem' }}>
            {isListingReady && (
              <StickyContainer $isSticky={isUserLoggedIn}>
                <Stack gap='6'>
                  {isUpgradeCTAVisible && (
                    <TabletAndUpOnlyBox>
                      <ListingUpgradeCTA name={listing?.user?.firstName!} />
                    </TabletAndUpOnlyBox>
                  )}
                  {isMessengerVisible && listing && (
                    <Messenger
                      isFreeUser={isFreeUser}
                      limitedApplicationsVariant={limitedApplicationsVariant}
                      listingId={listing.id}
                      hasConversationWithUser={listing.hasConversationWithRequester}
                      conversationPath={listing.conversationPath}
                      telesafeUrl={listing.telesafeUrl}
                      // @ts-ignore
                      recipient={listingUser}
                      // @ts-ignore
                      defaultConversationMessage={listing.conversationStarterMessage}
                      // @ts-ignore
                      avatar={listing.avatar}
                      city={listing.city}
                      state={listing.state}
                      similarListings={similarListings}
                      successfulMessageSentListener={refetch}
                    />
                  )}
                  {isListingCTAVisible && (
                    <TabletAndUpOnlyBox>
                      <ListingCTA listing={data?.listing} viewer={viewer} />
                    </TabletAndUpOnlyBox>
                  )}
                  {hasSimilarListings && (
                    <div>
                      <Stack gap='2'>
                        <Heading as='h2'>Similar Profiles Nearby</Heading>
                        <Text mt='0'>
                          Near {listingCityName}, {listingStateName}
                        </Text>
                        <Stack gap='4' role='list'>
                          {similarListings.map((listing, i) => (
                            <ProfileCard key={i} profile={listing} />
                          ))}
                        </Stack>
                      </Stack>
                    </div>
                  )}
                </Stack>

                <TabletAndUpOnlyBox minWidth='100%' justifyContent='center' mt={6} aria-hidden>
                  <AdSlot adUnit='listing-right-side' sizes={[[336, 280]]} />
                </TabletAndUpOnlyBox>

                <MobileOnlyBox
                  minWidth='100%'
                  display='inline-flex'
                  justifyContent='center'
                  mt={6}
                  aria-hidden
                >
                  <AdSlot adUnit='listing-mobile-bottom' sizes={[[300, 250]]} />
                </MobileOnlyBox>
              </StickyContainer>
            )}
          </Box>
        </Box>
      </ContentBlock>
    </DFPSlotsProvider>
  )
}

export default Listing
